import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Popper,
  RadioGroup,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import React, { useMemo, useState } from 'react';

import { useStyles } from './styles';
import { calendarWeek } from 'src/helpers/calendar/calendarWeek';
import moment from 'moment';
import { checkIsDisabledToSearch } from 'src/helpers/calendar/calendarRequests';
import { Spiner } from '../../Loaders/Spiner/Spiner';
import { TicketsModal } from '../CalendarModals/TicketsModal';
import { ScheduleModal } from '../CalendarModals/ScheduleModal';
import { OptionModal } from '../CalendarModals/OptionModal';
import { SchedulesBlock } from './components/SchedulesBlock';
import { DatesBlock } from './components/DatesBlock';
import { WeekBlock } from './components/WeekBlock';

import { useQuery } from '@apollo/client';
import { GET_TRIP } from 'src/api/calendar';
import { useDispatch, useSelector } from 'react-redux';
import { CustomAutocomplete } from '../../UI/CustomAutocomplete';
import { auth } from 'src/redux/Authentication/sliceAuthentication';
import { CustomRadio } from '../../UI/CustomRadio/CustomRadio';
import { ToggleDialog } from './ToggleDialog';
import {
  allowBtns,
  calendarOptions,
  setOption,
  setRate,
  setTicket,
  setTrip,
  setTripData,
  setType,
  tripData,
} from 'src/redux/Calendar/sliceCalendar';
import { useCalendarAutocomplete } from 'src/hooks/autocompletes/useCalendarAutocomplete';
import { setNotification } from 'src/redux/Notifications/sliceNotifications';

const CustomPopper = function (props: any) {
  const classes = useStyles();
  return <Popper {...props} className={classes.popper} placement="top" />;
};

export const CalendarFilters = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const { user } = useSelector(auth);
  const partner_id = +user['custom:partner_id'];
  const { trip_id, rate_id, type, option_type, ticket_type } =
    useSelector(calendarOptions);
  const data = useSelector(tripData);

  const { openCloseBtn } = useSelector(allowBtns);

  const { tripList, loading } = useCalendarAutocomplete();

  useQuery(GET_TRIP, {
    skip: !trip_id,
    onCompleted: ({ v1_activities_get }) => {
      dispatch(setTripData(v1_activities_get));
    },
    variables: {
      prm: { id: trip_id, user_id: +partner_id },
    },
    onError: (error) => {
      dispatch(
        setNotification({
          message: error.message,
          type: 'error',
        })
      );
    },
  });

  const [toggleState, setToggleState] = useState<{
    action?: 'open' | 'close';
    open: boolean;
  }>({
    open: false,
  });

  const onCloseToogleDialog = () => {
    setToggleState({
      open: false,
    });
  };

  const filteredTripsList = useMemo(() => {
    if (!partner_id) return tripList;
    return tripList.filter((i) => i.partner_id === partner_id);
  }, [tripList, partner_id]);

  const withRates = Boolean(data?.rates.length);
  const withOptions = Boolean(data?.additional_options?.length);

  const types = withRates
    ? ['ticket']
    : withOptions
    ? ['ticket', 'option', 'schedule']
    : ['ticket', 'schedule'];

  return (
    <Box className={classes.cardBox} marginTop="16px" width={1}>
      {loading && <Spiner fixed />}
      <ToggleDialog
        open={toggleState.open}
        action={toggleState.action || 'open'}
        handleClose={onCloseToogleDialog}
      />
      {/* 
      <Typography className={classes.cardTitle}>
        Detailed settings of availability
      </Typography>
      <Divider className={classes.cardDivider} /> */}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap={isMobile ? 'wrap' : 'nowrap'}
        gridGap={20}>
        <Box className={classes.inputWrapper} width={isMobile ? 1 : 1 / 2}>
          <CustomAutocomplete
            label="Trips"
            placeholder="Choose trip"
            className={classes.inputWrapper}
            options={filteredTripsList}
            onChange={(value: any) => dispatch(setTrip(value?.id))}
            value={filteredTripsList.find((i) => i.id === trip_id)}
            loading={loading}
            deletable={true}
            CustomPopper={CustomPopper}
            renderOption={(option: any) => (
              <Box width={1} className={classes.autocompleteItem}>
                <Box>{option.name}</Box>
              </Box>
            )}
          />
        </Box>
        {withRates && (
          <Box className={classes.inputWrapper} width={isMobile ? 1 : 1 / 2}>
            <CustomAutocomplete
              disableClearable
              value={rate_id || 0}
              placeholder="No rate"
              className={classes.label}
              onChange={(value: { id: number; title: string }) => {
                dispatch(setRate(+value.id));
              }}
              label="Rate"
              options={
                data?.rates?.map((i) => ({ id: +i.id, title: i.name })) || []
              }
              getOptionLabel={(option) =>
                data?.rates?.find((i) => +i.id === +option)?.name || ''
              }
              loading={loading}
              CustomPopper={CustomPopper}
              renderOption={(option: any) => (
                <Box width={1} className={classes.autocompleteItem}>
                  <Box>{option.title}</Box>
                </Box>
              )}
            />
          </Box>
        )}
        <Box width={isMobile ? 1 : 1 / 2}>
          <RadioGroup
            className={classes.radioGroup}
            value={type || 'ticket'}
            onChange={(_, value) =>
              dispatch(setType(value as 'ticket' | 'schedule' | 'option'))
            }>
            <FormControlLabel
              className={classes.radioItem}
              value="ticket"
              control={<CustomRadio />}
              label="Ticket"
              disabled={!trip_id}
            />
            <FormControlLabel
              className={classes.radioItem}
              value="schedule"
              control={<CustomRadio />}
              label="Schedule"
              disabled={
                !trip_id || !data?.schedules?.length || !!data?.is_integrated
              }
            />
            <FormControlLabel
              className={classes.radioItem}
              value="option"
              control={<CustomRadio />}
              label="Option"
              disabled={
                !types.includes('option') ||
                !withOptions ||
                !!data?.is_integrated
              }
            />
          </RadioGroup>
        </Box>
      </Box>

      <DatesBlock />

      <WeekBlock />

      <SchedulesBlock />

      {type === 'schedule' && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          style={{ gap: '10px' }}
          width={1}
          mt={2}
          flexWrap="wrap">
          <Button
            variant="outlined"
            color="default"
            className={classes.cancelButton}
            style={{
              padding: '5px 45px',
            }}
            disabled={!openCloseBtn}
            onClick={() => setToggleState({ open: true, action: 'close' })}>
            Disable Selected Schedules
          </Button>
          <Button
            variant="outlined"
            color="primary"
            className={classes.saveButton}
            style={{
              padding: '5px 20px',
            }}
            disabled={!openCloseBtn}
            onClick={() => setToggleState({ open: true, action: 'open' })}>
            Activate Selected Schedules
          </Button>
        </Box>
      )}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{ gap: '20px' }}
        mt={6}
        flexWrap="wrap">
        {type === 'ticket' && !!data?.tickets?.length && (
          <>
            <Box width={isMobile ? 1 : 1 / 2} className={classes.inputWrapper}>
              <CustomAutocomplete
                disableClearable
                value={ticket_type || 0}
                label="Ticket Type"
                placeholder="Choose ticket type"
                className={classes.label}
                onChange={(value: { id: number; title: string }) =>
                  dispatch(setTicket(+value.id))
                }
                options={
                  data.tickets?.map((i) => ({
                    id: +i.id,
                    title: i.name,
                  })) || []
                }
                getOptionLabel={(option) =>
                  data.tickets?.find((i) => i.id === option)?.name || ''
                }
                loading={loading}
                CustomPopper={CustomPopper}
                renderOption={(option: any) => (
                  <Box width={1} className={classes.autocompleteItem}>
                    <Box>{option.title}</Box>
                  </Box>
                )}
              />
            </Box>
            <Box
              display="flex"
              alignItems="center"
              style={{ gap: '10px' }}
              flexWrap="wrap">
              <Button
                variant="outlined"
                color="default"
                className={classes.cancelButton}
                style={{
                  padding: '5px 45px',
                }}
                disabled={!openCloseBtn}
                onClick={() => setToggleState({ open: true, action: 'close' })}>
                Disable All Tickets
              </Button>
              <Button
                variant="outlined"
                color="primary"
                className={classes.saveButton}
                style={{
                  padding: '5px 20px',
                }}
                disabled={!openCloseBtn}
                onClick={() => setToggleState({ open: true, action: 'open' })}>
                Activate All Tickets
              </Button>
            </Box>
          </>
        )}
        {type === 'option' && !!data?.additional_options?.length && (
          <>
            <Box width={isMobile ? 1 : 1 / 2} className={classes.inputWrapper}>
              <CustomAutocomplete
                disableClearable
                value={option_type || 0}
                label="Option Type"
                placeholder="Choose option type"
                className={classes.label}
                onChange={(value: { id: number; title: string }) => {
                  dispatch(setOption(+value.id));
                }}
                options={
                  data.additional_options?.map((i) => ({
                    id: +i.id,
                    title: i.name,
                  })) || []
                }
                getOptionLabel={(option) =>
                  data.additional_options?.find((i) => i.id === option)?.name ||
                  ''
                }
                loading={loading}
                CustomPopper={CustomPopper}
                renderOption={(option: any) => (
                  <Box width={1} className={classes.autocompleteItem}>
                    <Box>{option.title}</Box>
                  </Box>
                )}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
